import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dropdown, Menu, MenuProps } from "antd";
import {
  LogoutOutlined,
  UserOutlined,
  SettingOutlined,
  CalculatorOutlined,
  FileTextOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { LogOut } from "../../../features/AuthenticateView/pages/Login/Login.types";
import { REFRESH_TOKEN_KEY, TOKEN_KEY } from "../../utils/SessionConstants";
import { LogoutService } from "../../../features/AuthenticateView/services/LoginService";
import { getUserDisplayName, getUserInitials } from "../../../utils/authUtils";
import styles from "./Sidebar.module.css";

const NAV_ITEMS: MenuProps["items"] = [
  {
    key: "clients",
    icon: <UserOutlined />,
    label: "Clients",
    children: [
      { key: "/clients", label: "Master Clients" },
      { key: "/engagements", label: "Engagements" },
      { key: "/entities", label: "Entities" },
    ],
  },
  {
    key: "configurations",
    icon: <SettingOutlined />,
    label: "Configurations",
    children: [
      { key: "/configurations/calc-profile", label: "Calculation Profile" },
      { key: "/configurations/templates", label: "Template Library" },
      { key: "/settings/calculations", label: "Standard Calculations" },
    ],
  },
  {
    key: "calculations",
    icon: <CalculatorOutlined />,
    label: "Calculations",
    children: [
      { key: "/calculations/project", label: "Project" },
      { key: "/calculations/cascade", label: "Tier Flow" },
      { key: "/calculations/roll-forward", label: "Roll Forward" },
      { key: "/calculations/upload", label: "Upload Input" },
      { key: "/calculations/account-mapping", label: "CoA Mapping" },
    ],
  },
  {
    key: "/tax-form",
    icon: <FileTextOutlined />,
    label: "Generate Tax Form",
  },
  {
    key: "settings",
    icon: <ToolOutlined />,
    label: "Settings",
    children: [
      { key: "/settings/users", label: "User Management" },
      { key: "/settings/audit", label: "Audit Log" },
    ],
  },
];

function getOpenKey(pathname: string): string {
  if (pathname.startsWith("/clients") || pathname.startsWith("/overview") || pathname.startsWith("/engagements") || pathname.startsWith("/entities") || pathname.startsWith("/activities")) return "clients";
  if (pathname.startsWith("/configurations") || pathname.startsWith("/settings/calculations")) return "configurations";
  if (pathname.startsWith("/financials")) return "financials";
  if (pathname.startsWith("/calculations")) return "calculations";
  if (pathname.startsWith("/settings")) return "settings";
  return "";
}

function getSelectedKey(pathname: string): string {
  const match = [
    "/clients", "/overview", "/engagements", "/entities", "/activities",
    "/configurations/settings", "/configurations/calc-profile", "/configurations/templates",
    "/settings/calculations",
    "/financials/capital", "/financials/m1", "/financials/assets",
    "/calculations/project", "/calculations/cascade",
    "/calculations/roll-forward", "/calculations/upload", "/calculations/account-mapping",
    "/tax-form",
    "/settings/users", "/settings/audit",
  ].find((p) => pathname.startsWith(p));
  return match ?? pathname;
}

const NAV_COLLAPSED_KEY = "main-nav-collapsed";

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const displayName = getUserDisplayName();
  const initials    = getUserInitials();
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const k = getOpenKey(pathname);
    return k ? [k] : [];
  });
  const [navCollapsed, setNavCollapsed] = useState<boolean>(
    () => localStorage.getItem(NAV_COLLAPSED_KEY) === "true",
  );
  const [navHovered, setNavHovered] = useState(false);

  useEffect(() => {
    const k = getOpenKey(pathname);
    if (k && !openKeys.includes(k)) setOpenKeys((prev) => [...prev, k]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const toggleNav = useCallback(() => {
    setNavCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(NAV_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === "\\" && !e.shiftKey) {
        e.preventDefault();
        toggleNav();
      } else if (e.key === "\\" && e.shiftKey) {
        e.preventDefault();
        setNavCollapsed(true);
        localStorage.setItem(NAV_COLLAPSED_KEY, "true");
      } else if (e.key === "Escape") {
        setNavCollapsed(false);
        localStorage.setItem(NAV_COLLAPSED_KEY, "false");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleNav]);

  const handleLogout = () => {
    const logOut: LogOut = {
      token: localStorage.getItem(TOKEN_KEY),
      refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
      isConsentAccepted: null,
    };
    LogoutService(logOut);
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  const userMenuItems: MenuProps["items"] = [
    { key: "username", label: displayName, disabled: true },
    {
      key: "logout",
      label: "Log out",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  const renderNavBody = (expanded: boolean, onNavigate?: () => void) => (
    <>
      <div className={styles.sidebarLogo}>
        <img
          className={styles.logoImg}
          src="/propel-logo-white.png"
          alt="Propel"
          onClick={() => navigate("/clients")}
        />
        {expanded && <div className={styles.subtitle}>Calc Engine</div>}
      </div>

      <div className={styles.sidebarNav}>
        <Menu
          mode="inline"
          theme="dark"
          inlineCollapsed={!expanded}
          items={NAV_ITEMS}
          selectedKeys={[getSelectedKey(pathname)]}
          openKeys={expanded ? openKeys : []}
          onOpenChange={setOpenKeys}
          onClick={({ key }) => {
            if (key.startsWith("/")) {
              navigate(key);
              onNavigate?.();
            }
          }}
          style={{ background: "transparent", border: "none", fontSize: 11.5 }}
        />
      </div>

      <button
        onClick={toggleNav}
        className={styles.navToggle}
        title={navCollapsed ? "Expand menu (\\)" : "Collapse menu (\\)"}
        aria-label={navCollapsed ? "Expand menu" : "Collapse menu"}
      >
        {navCollapsed && !navHovered ? "›" : "‹"}
      </button>

      <div className={styles.sidebarFooter}>
        <Dropdown menu={{ items: userMenuItems }} trigger={["click"]}>
          <div className={styles.userInfo}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #F26522, #c44d1a)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
                color: '#fff',
                flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              {initials}
            </div>
            {expanded && <span className={styles.userName}>{displayName}</span>}
          </div>
        </Dropdown>
      </div>
    </>
  );

  return (
    <aside
      className={`${styles.sidebar}${navCollapsed ? ` ${styles.sidebarCollapsed}` : ''}`}
      onMouseEnter={() => navCollapsed && setNavHovered(true)}
      onMouseLeave={() => setNavHovered(false)}
    >
      {renderNavBody(!navCollapsed)}

      {navCollapsed && (
        <div className={`${styles.navOverlay}${navHovered ? ` ${styles.navOverlayHovered}` : ''}`}>
          {renderNavBody(true, () => setNavHovered(false))}
        </div>
      )}
    </aside>
  );
};
